import type { ListPageConfig, SortOption, TabOption } from '../types'
import { formatStatusLabel } from '@/lib/status_colors'
import { COUNTRY_NAMES } from '@/lib/country_names'

const COMPANY_STATUS_OPTIONS = [
  'ACTIVE', 'DORMANT', 'DISSOLVED', 'IN_LIQUIDATION',
  'STRUCK_OFF', 'SUSPENDED', 'CONVERTED', 'CLOSED', 'NOT_CONFIRMED',
].map((s) => ({ value: s, label: formatStatusLabel(s)! }))

const ENTITY_TYPE_OPTIONS = [
  'CORPORATION', 'LLC', 'LLP', 'PARTNERSHIP',
  'SOLE_PROPRIETORSHIP', 'COOPERATIVE', 'FOUNDATION',
  'TRUST', 'NON_PROFIT', 'OTHER',
].map((t) => ({ value: t, label: formatStatusLabel(t)! }))

// Sourced from the shared COUNTRY_NAMES catalogue, not a literal local to this file. The 21-entry
// map that used to live here omitted SA and QA while listing DE/BR/JP, which have no entities — so
// Saudi and Qatari entities were unreachable through this filter and rendered as bare ISO codes in
// the dashboard's Entities-by-Jurisdiction card, which read the same map. `country_of_incorporation`
// is a free ISO code off the 249-row places table, so any hand-kept subset here drifts by
// construction. Sorted by label because the list is long enough that insertion order is unusable.
const COUNTRY_OPTIONS = Object.entries(COUNTRY_NAMES)
  .map(([code, name]) => ({ value: code, label: name }))
  .sort((a, b) => a.label.localeCompare(b.label))

/**
 * The entity-type tabs. They live beside the fields, not in EntitiesPage, because the route's
 * `validateSearch` needs them too — a tab value the router does not know about is stripped from
 * the URL on the next navigation.
 */
export const ENTITY_TYPE_TAB_OPTIONS: TabOption[] = [
  { key: 'all',         label: 'All Entities' },
  { key: 'company',     label: 'Companies' },
  { key: 'partnership', label: 'Partnerships' },
  { key: 'committee',   label: 'Committees' },
  { key: 'other',       label: 'Other Entities' },
]

/**
 * This page's sort is entirely page-local (re-sorts the loaded page client-side — there is no
 * server `sort` argument on the entities query), but it is still URL-synced through
 * `useListPageState`/`FilterBar` like every other sort config, so the control renders through the
 * one canonical `SortControl` instead of a page-local dropdown (EW-T sidebar-filter-rollout).
 */
export const ENTITY_SORT_OPTIONS: SortOption[] = [
  { key: 'priority', label: 'Priority first' },
  { key: 'name', label: 'Legal name' },
  { key: 'health', label: 'Lowest record health' },
  { key: 'obligation', label: 'Earliest obligation' },
]

export const entitiesFilterConfig: ListPageConfig = {
  routeId: '/entities/',
  pageId: 'entities',
  pagination: { pageSize: 20 },
  tabs: { options: ENTITY_TYPE_TAB_OPTIONS, default: 'all', urlKey: 'type' },
  sort: { options: ENTITY_SORT_OPTIONS, default: { key: 'priority', order: 'asc' } },
  fields: [
    {
      id: 'search',
      label: 'Search',
      type: 'search',
      placeholder: 'Search entities...',
      serverSide: true,
    },
    {
      id: 'countries',
      label: 'Country',
      type: 'multi-select',
      options: COUNTRY_OPTIONS,
      serverSide: true,
    },
    {
      id: 'entityTypes',
      label: 'Type',
      type: 'multi-select',
      options: ENTITY_TYPE_OPTIONS,
      responsiveHide: true,
      serverSide: true,
    },
    {
      id: 'statuses',
      label: 'Status',
      type: 'multi-select',
      options: COMPANY_STATUS_OPTIONS,
      serverSide: true,
    },
    {
      // #9: filter by managing client (LegalEntityFilterGqlInput.managedBy: String).
      // Options are populated dynamically in EntitiesPage via useRelatedOrgs(persona:"CLIENT").
      // URL-synced automatically by useListPageState (select type → single string param).
      id: 'managedBy',
      label: 'Client',
      type: 'select',
      options: [],
      serverSide: true,
      placeholder: 'Filter by client...',
    },
    {
      id: 'showArchived',
      label: 'Show Archived',
      type: 'toggle',
      defaultValue: false,
    },
  ],
}
